import {
  CarouselLayout,
  ConnectionStateToast,
  FocusLayout,
  FocusLayoutContainer,
  GridLayout,
  LayoutContextProvider,
  ParticipantTile,
  RoomAudioRenderer,
} from '@livekit/components-react';
import { useCreateLayoutContext } from '@livekit/components-react';
import { usePinnedTracks, useTracks } from '@livekit/components-react';
import { Chat } from '@livekit/components-react';
import { isEqualTrackRef, isTrackReference, isWeb, log } from '@livekit/components-core';
import { RoomEvent, Track } from 'livekit-client';
import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { ControlBarLocal } from './ControlBarLocal';
import { ErrorBoundary } from './ErrorBoundary';

// Hook to detect mobile devices
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

export function VideoConferenceLocal({ onLeaveIntent, ...props }) {
  const [widgetState, setWidgetState] = useState({
    showChat: false,
    unreadMessages: 0,
  });

  const isMobile = useIsMobile();
  const lastAutoFocusedScreenShareTrack = useRef(null);

  // Keep camera placeholders so attendees are visible even before they publish video.
  // updateOnlyOn must include participant lifecycle events so new attendees appear immediately.
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    {
      updateOnlyOn: [
        RoomEvent.ParticipantConnected,
        RoomEvent.ParticipantDisconnected,
        RoomEvent.TrackPublished,
        RoomEvent.TrackUnpublished,
        RoomEvent.TrackMuted,
        RoomEvent.TrackUnmuted,
        RoomEvent.TrackSubscribed,
        RoomEvent.TrackUnsubscribed,
        RoomEvent.LocalTrackPublished,
        RoomEvent.LocalTrackUnpublished,
        RoomEvent.ActiveSpeakersChanged,
      ],
      onlySubscribed: false,
    },
  );

  const widgetUpdate = (state) => {
    log.debug('updating widget state', state);
    setWidgetState(state);
  };

  const layoutContext = useCreateLayoutContext();

  const screenShareTracks = tracks
    .filter(isTrackReference)
    .filter((track) => track.publication.source === Track.Source.ScreenShare);
  const screenShareTrackState = useMemo(
    () =>
      screenShareTracks
        .map((ref) => `${ref.publication.trackSid}_${ref.publication.isSubscribed}`)
        .join(),
    [screenShareTracks],
  );

  const focusTrack = usePinnedTracks(layoutContext)?.[0];
  const carouselTracks = tracks.filter((track) => !isEqualTrackRef(track, focusTrack));
  
  // On mobile, only use focus layout for screen shares, not for camera pins
  const shouldUseFocusLayout = focusTrack && (
    isMobile 
      ? focusTrack.publication.source === Track.Source.ScreenShare 
      : true
  );

  useEffect(() => {
    const currentAutoFocusSid = lastAutoFocusedScreenShareTrack.current?.publication?.trackSid;
    const currentAutoFocusTrack = screenShareTracks.find(
      (track) => track.publication.trackSid === currentAutoFocusSid,
    );
    const firstSubscribedScreenShareTrack = screenShareTracks.find(
      (track) => track.publication.isSubscribed,
    );

    if (!firstSubscribedScreenShareTrack) {
      if (currentAutoFocusSid) {
        log.debug('Auto clearing screen share focus.');
        layoutContext.pin.dispatch?.({ msg: 'clear_pin' });
        lastAutoFocusedScreenShareTrack.current = null;
      }
      return;
    }

    if (!currentAutoFocusTrack || !currentAutoFocusTrack.publication.isSubscribed) {
      log.debug('Auto set screen share focus:', { newScreenShareTrack: firstSubscribedScreenShareTrack });
      layoutContext.pin.dispatch?.({
        msg: 'set_pin',
        trackReference: firstSubscribedScreenShareTrack,
      });
      lastAutoFocusedScreenShareTrack.current = firstSubscribedScreenShareTrack;
    }
  }, [screenShareTracks, screenShareTrackState, focusTrack?.publication?.trackSid, layoutContext.pin]);

  // On mobile, prevent any camera pins - always use grid layout (no fullscreen, no freeze)
  useLayoutEffect(() => {
    if (isMobile && focusTrack && focusTrack.publication?.source === Track.Source.Camera) {
      log.debug('Mobile: Clearing camera pin to prevent fullscreen and video freeze');
      layoutContext.pin.dispatch?.({ msg: 'clear_pin' });
    }
  }, [isMobile, focusTrack, layoutContext.pin]);

  return (
    <div className="lk-video-conference" {...props}>
      {isWeb() && (
        <LayoutContextProvider
          value={layoutContext}
          onWidgetChange={widgetUpdate}
        >
          <div className="lk-video-conference-inner">
            {!shouldUseFocusLayout ? (
              <div className="lk-grid-layout-wrapper">
                <GridLayout tracks={tracks}>
                  <ParticipantTile />
                </GridLayout>
              </div>
            ) : (
              <div className={`lk-focus-layout-wrapper ${isMobile ? 'mobile-focus-layout' : ''}`}>
                <FocusLayoutContainer>
                  <CarouselLayout tracks={carouselTracks}>
                    <ParticipantTile />
                  </CarouselLayout>
                  {focusTrack && <FocusLayout trackRef={focusTrack} />}
                </FocusLayoutContainer>
              </div>
            )}

            {/* ✅ Hides camera/mic buttons; keeps chat */}
            <ErrorBoundary>
              <ControlBarLocal controls={{ chat: true }} onLeaveIntent={onLeaveIntent}/>
            </ErrorBoundary>

          </div>

          <Chat
            style={{ display: widgetState.showChat ? 'grid' : 'none' }}
          />
        </LayoutContextProvider>
      )}

      <RoomAudioRenderer />
      <ConnectionStateToast />
    </div>
  );
}
